import { useControllableProp, useMergeRefs } from '@chakra-ui/react';
import { getValidChildren } from '@chakra-ui/react-utils';
import { ComponentWithAs, forwardRef, useMultiStyleConfig } from '@chakra-ui/system';
import { callAllHandlers, dataAttr } from '@chakra-ui/utils';
import { FormControl } from '@lego/klik-ui-form-control';
import { Date } from '@lego/klik-ui-icons';
import { Input } from '@lego/klik-ui-input';
import { Stack } from '@lego/klik-ui-stack';
import * as React from 'react';
import {
  IDateInputProps,
  IDatePickerProps,
  IDateRangePickerDatePicker,
  IDateRangePickerProps,
} from './DatePickerProps';

export const DateInputNative = forwardRef<IDateInputProps, 'input'>((props, ref) => {
  const {
    type = 'date',
    size = 'md',
    onChange: onChangeProp = (_date: string) => void {},
    value: valueProp,
    defaultValue = '',
    ...rest
  } = props;

  const styles = useMultiStyleConfig('DatepickerNative', { size });

  const [valueState, setValue] = React.useState(defaultValue);
  const [isValueControlled, value] = useControllableProp(valueProp, valueState);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isValueControlled) {
        setValue(event.target.value);
      }

      onChangeProp(event.target.value);
    },
    [isValueControlled, onChangeProp]
  );

  return (
    <Input.Group size={size}>
      <Input
        {...rest}
        data-has-value={dataAttr(!!value)}
        onChange={handleChange}
        ref={ref}
        sx={styles.input}
        type={type}
        value={value}
      />
      <Input.RightElement pointerEvents="none">
        <Date sx={styles.icon} />
      </Input.RightElement>
    </Input.Group>
  );
});

export const DatePickerNative = forwardRef<IDatePickerProps, 'div'>((props, ref) => {
  const {
    errorMessageInvalid = 'Invalid date',
    errorMessageRequired = 'Required field',
    flex,
    helperText,
    isInvalid: isInvalidProp,
    label,
    min,
    max,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    size,
    ...rest
  } = props;

  const internalRef = React.useRef<HTMLInputElement>();
  const refs = useMergeRefs(internalRef, ref);

  const getInputValidity = () => {
    const validity = {
      isOutOfRange: false,
      isValueMissing: false,
      isValid: true,
    };

    const inputValidity = internalRef.current?.validity;

    if (!inputValidity) {
      return validity;
    }

    validity.isOutOfRange = inputValidity.rangeUnderflow || inputValidity.rangeOverflow;
    validity.isValueMissing = inputValidity.valueMissing;
    validity.isValid = inputValidity.valid;

    return validity;
  };

  const [isInvalidState, setIsInvalid] = React.useState(isInvalidProp ?? false);
  const [isInvalidControlled, isInvalid] = useControllableProp(isInvalidProp, isInvalidState);
  const [errorMessage, setErrorMessage] = React.useState(errorMessageRequired);

  // Set initial validation state and when min/max
  // changes, e.g. when used in date range picker:
  React.useLayoutEffect(() => {
    const { isOutOfRange } = getInputValidity();

    if (isOutOfRange) {
      setErrorMessage(errorMessageInvalid);
    }

    if (!isInvalidControlled) {
      setIsInvalid(isOutOfRange);
    }
  }, [errorMessageInvalid, isInvalidControlled, min, max]);

  const handleChange = React.useCallback(
    (_date: string) => {
      const inputValidity = getInputValidity();

      if (inputValidity.isValueMissing) {
        setErrorMessage(errorMessageRequired);
      } else if (inputValidity.isOutOfRange) {
        setErrorMessage(errorMessageInvalid);
      } else {
        setErrorMessage('');
      }

      if (!isInvalidControlled) {
        // Only update validation state while typing/picking if it goes from invalid => valid.
        // Otherwise state will be reflected on blur:
        if (isInvalid && inputValidity.isValid) {
          setIsInvalid(false);
        }
      }
    },
    [isInvalidControlled, isInvalid]
  );

  const handleBlur = React.useCallback(
    (_event: React.FocusEvent<HTMLInputElement>) => {
      if (!isInvalidControlled) {
        const isInputInvalid = !getInputValidity().isValid;

        // Only update internal state and re-render if input state has changed:
        if (isInvalid !== isInputInvalid) {
          setIsInvalid(isInputInvalid);
        }
      }
    },
    [isInvalidControlled, isInvalid]
  );

  return (
    <FormControl flex={flex} isInvalid={isInvalid} size={size}>
      <FormControl.Label size={size}>{label}</FormControl.Label>
      <DateInputNative
        {...rest}
        max={max}
        min={min}
        onBlur={callAllHandlers(onBlurProp, handleBlur)}
        onChange={callAllHandlers(onChangeProp, handleChange)}
        ref={refs}
        size={size}
      />
      {helperText && !isInvalid && <FormControl.HelperText>{helperText}</FormControl.HelperText>}
      {errorMessage && (
        <FormControl.ErrorMessage size={size}>{errorMessage}</FormControl.ErrorMessage>
      )}
    </FormControl>
  );
});

// Need to export for storybook argsTable to pick up the API:
export const DateRangePickerNativeDatePicker = forwardRef<IDateRangePickerDatePicker, 'div'>(
  ({ defaultValue: _defaultValue, ...rest }, ref) => {
    return <DatePickerNative {...rest} ref={ref} />;
  }
);

type IDateRangePickerNativeComponent = ComponentWithAs<'div', IDateRangePickerProps> & {
  DatePicker: typeof DateRangePickerNativeDatePicker;
};

export const DateRangePickerNative = forwardRef<IDateRangePickerProps, 'div'>((props, ref) => {
  const {
    children,
    min: minProp,
    onChange: onChangeProp = (_dates: string[]) => void {},
    value: valueProp,
    defaultValue = ['', ''],
    ...rest
  } = props;

  const [valueState, setValue] = React.useState(defaultValue);
  const [isValueControlled, value] = useControllableProp(valueProp, valueState);

  const [startDate, endDate] = value;

  const handleStartDateChange = React.useCallback(
    (nextStartDate: string) => {
      const [, currentEndDate] = value;

      if (!isValueControlled) {
        setValue([nextStartDate, currentEndDate]);
      }

      onChangeProp([nextStartDate, currentEndDate]);
    },
    [isValueControlled, value, onChangeProp]
  );

  const handleEndDateChange = React.useCallback(
    (nextEndDate: string) => {
      const [currentStartDate] = value;

      if (!isValueControlled) {
        setValue([currentStartDate, nextEndDate]);
      }

      onChangeProp([currentStartDate, nextEndDate]);
    },
    [isValueControlled, value, onChangeProp]
  );

  const validChildren = getValidChildren(children).filter(
    (childNode): childNode is React.ReactElement<IDatePickerProps> =>
      childNode.type === DateRangePickerNativeDatePicker
  );

  return (
    <Stack direction={{ base: 'column', md: 'row' }} ref={ref} spacing="4">
      {validChildren.map((child, index) => {
        if (index === 0) {
          return React.cloneElement(child, {
            ...rest,
            flex: '1',
            min: minProp,
            onChange: handleStartDateChange,
            value: startDate,
          });
        }

        if (index === 1) {
          return React.cloneElement(child, {
            ...rest,
            flex: '1',
            min: startDate || minProp,
            onChange: handleEndDateChange,
            value: endDate,
          });
        }

        return undefined;
      })}
    </Stack>
  );
}) as IDateRangePickerNativeComponent;

DateRangePickerNative.DatePicker = DateRangePickerNativeDatePicker;
